import React, { useState } from "react";
import PropTypes from "prop-types";
import { TextField, Typography } from "@material-ui/core";
import { useStyles } from "./style";
import * as Yup from "yup";
import clsx from "clsx";
import { useFormContext, Controller } from "react-hook-form";
import { Tooltip } from "@material-ui/core";
import ChangeOneParkEmailDialog from "./ChangeOneParkEmailDialog";

export const EmailSubformSchema = Yup.object().shape({
    isRequired: Yup.boolean(),
    type: Yup.string()
        .oneOf(["Unset", "Personal", "Work"])
        .default("Personal")
        .notRequired(),
    emailAddress: Yup.string().trim('No leading or trailing whitespaces...')
        .email()
        .when("isRequired", {
            is: (value) => value === true,
            then: Yup.string()
                .email("Must be a valid email")
                .required("* Required"),
            otherwise: Yup.string()
                .email("Must be a valid email")
                .notRequired(),
        }),
});

const EmailSubform = ({
    index,
    disabled,
    isOneParkUser,
    canEditOneParkEmail,
    emailChangeStatus,
    pendingEmail,
    currentEmail,
    onChangeOneParkEmail
}) => {
    const classes = useStyles();
    const { control, setValue, trigger, formState: { errors } } = useFormContext();
    const OneParkUser = Boolean(isOneParkUser);

    const canOpenOneParkDialog =
        OneParkUser && canEditOneParkEmail && !disabled && typeof onChangeOneParkEmail === "function";

    const isEmailReadOnly = disabled || OneParkUser;
    const isEmailDisabledAttr = disabled && !canOpenOneParkDialog;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [optimisticPending, setOptimisticPending] = useState(null);
    const effectiveStatus = optimisticPending ? "pending" : emailChangeStatus;
    const effectivePendingEmail = optimisticPending ? optimisticPending.pendingEmail : pendingEmail;
   
    const isPending = effectiveStatus === "pending";

    const formatTextField = (e) => {
        const val = (e.target.value || "").replace(/\s+/gi, " ");
        setValue(e.target.name, val.trim());
        trigger(e.target.name);
    };

    const getError = () => {
        if (errors.contactInfo?.emails && errors.contactInfo?.emails[index]?.emailAddress) {
            return errors.contactInfo?.emails[index]?.emailAddress.message.replace(`contactInfo.emails[${index}].emailAddress m`, 'M');
        }
    }

    const openDialog = () => {
        if (canOpenOneParkDialog) setDialogOpen(true);
    };

    return (
        <Controller
            name={`contactInfo.emails.${index}.emailAddress`}
            control={control}
            render={({ field }) => {
               const textField = (
               <TextField
                    {...field}
                    size="small"
                    className={clsx("email-address", classes.flexInput)}
                    label="Email"
                    variant="outlined"
                    onBlur={formatTextField}
                    onClick={canOpenOneParkDialog ? openDialog : undefined}
                    error={errors.contactInfo?.emails && errors.contactInfo?.emails[index]?.emailAddress ? true : false}
                    helperText={getError()}
                    InputProps={{
                        readOnly: Boolean(isEmailReadOnly),
                        "aria-readonly": Boolean(isEmailReadOnly),
                        disabled: Boolean(isEmailDisabledAttr),
                        style: canOpenOneParkDialog ? { cursor: "pointer" } : undefined,
                        onClick: canOpenOneParkDialog ? openDialog : undefined,
                    }}
               />
               );

               const fieldWithTooltip = OneParkUser ? (
                    <Tooltip title="This access holder is associated with a ONEpark account.">
                        <div>{textField}</div>
                    </Tooltip>
               ) : (
                    textField
               );

               return (
                    <div>
                        {fieldWithTooltip}
                        {isPending && (
                            <div className={clsx("email-indicators", classes.indicatorContainer)}>
                                <Typography
                                    variant="caption"
                                    className={clsx("pending-verification-indicator", classes.pendingText)}
                                >
                                    {effectivePendingEmail ? (
                                        <>
                                            <span className={classes.pendingEmail}>{effectivePendingEmail}</span>
                                            {" is pending for verification."}
                                        </>
                                    ) : (
                                        "A new email is waiting for verification. The email will not change until verification completes."
                                    )}
                                </Typography>
                            </div>
                        )}

                        {canOpenOneParkDialog && (
                            <ChangeOneParkEmailDialog
                                open={dialogOpen}
                                onClose={() => setDialogOpen(false)}
                                currentEmail={currentEmail || field.value}
                                onChangeOneParkEmail={onChangeOneParkEmail}
                                onInitiated={(submittedEmail) =>
                                    setOptimisticPending({ pendingEmail: submittedEmail })
                                }
                            />
                        )}
                    </div>
               );
            }}
        />
    );
};

EmailSubform.defaultProps = {
    disabled: false,
    canEditOneParkEmail: false,
    emailChangeStatus: "",
    pendingEmail: "",
    currentEmail: "",
    onChangeOneParkEmail: undefined,
};

EmailSubform.propTypes = {
    index: PropTypes.number,
    disabled: PropTypes.bool,
    isOneParkUser: PropTypes.bool,
    canEditOneParkEmail: PropTypes.bool,
    emailChangeStatus: PropTypes.string,
    pendingEmail: PropTypes.string,
    currentEmail: PropTypes.string,
    onChangeOneParkEmail: PropTypes.func,
};

export default EmailSubform;
